import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatINR } from "@/lib/api";
import Header from "@/components/Header";
import { useI18n } from "@/context/I18nContext";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  ArrowRight, BatteryCharging, Wallet, Users, Trophy, ShieldCheck, Bike,
  Calculator, MessageCircle, CalendarDays, X, CheckCircle2, Sparkles, Ticket, Crown, Play, Volume2, VolumeX,
} from "lucide-react";

const HERO_IMG =
  "https://images.unsplash.com/photo-1558980664-10e7170b5df9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwzfHxlbGVjdHJpYyUyMG1vdG9yY3ljbGV8ZW58MHx8fHwxNzg2NjMzNTI3fDA&ixlib=rb-4.1.0&q=85";
const WHATSAPP_NUMBER = "918722915521";
const ANNUAL_RATE = 0.11;
const TIME_SLOTS = ["10:00 AM", "11:00 AM", "12:00 PM", "3:00 PM", "4:00 PM", "5:00 PM", "6:00 PM"];

function calcEmi(price, months, downPct) {
  const loan = price * (1 - downPct / 100);
  const r = ANNUAL_RATE / 12;
  const emi = (loan * r * Math.pow(1 + r, months)) / (Math.pow(1 + r, months) - 1);
  return Math.round(emi);
}

// Silent lead capture — fire and forget
function captureLead(payload) {
  api.post("/leads", payload).catch(() => {});
}

// Format today+n date to YYYY-MM-DD (for min attribute)
function todayISO(offsetDays = 0) {
  const d = new Date();
  d.setDate(d.getDate() + offsetDays);
  return d.toISOString().split("T")[0];
}

function EmiWidget({ price }) {
  const { t } = useI18n();
  const [months, setMonths] = useState(24);
  const [downPct, setDownPct] = useState(20);
  const emi = calcEmi(price, months, downPct);
  const dpAmount = Math.round(price * downPct / 100);
  return (
    <div className="mt-4 pt-4 border-t border-white/5" data-testid="emi-widget">
      <div className="flex items-center justify-between text-[10px] uppercase tracking-[0.2em] text-white/40 font-bold mb-2">
        <span className="flex items-center gap-1.5"><Calculator className="w-3 h-3" /> {t("monthly_emi")}</span>
      </div>
      <div className="font-display font-black text-2xl text-[#00FF66]" data-testid="emi-amount">
        {formatINR(emi)}
        <span className="text-[10px] text-white/40 font-normal ml-1">{t("per_month")}</span>
      </div>
      <div className="mt-3 space-y-3">
        <div>
          <div className="flex justify-between text-[10px] text-white/50 font-bold uppercase tracking-[0.15em] mb-1">
            <span>{t("down_payment")}</span>
            <span className="text-white/80">{downPct}% · {formatINR(dpAmount)}</span>
          </div>
          <Slider value={[downPct]} onValueChange={(v) => setDownPct(v[0])} min={10} max={50} step={5} data-testid="dp-slider" />
        </div>
        <div>
          <div className="flex justify-between text-[10px] text-white/50 font-bold uppercase tracking-[0.15em] mb-1">
            <span>{t("tenure")}</span>
            <span className="text-white/80">{months} {t("months")}</span>
          </div>
          <Slider value={[months]} onValueChange={(v) => setMonths(v[0])} min={6} max={36} step={6} data-testid="emi-slider" />
        </div>
      </div>
      <div className="mt-2 text-[10px] text-white/40">{t("emi_note")}</div>
    </div>
  );
}

function WhatsAppButton({ model, className = "" }) {
  const { t, lang } = useI18n();
  const handleClick = () => {
    captureLead({ model_id: model.id, model_name: model.name, source: "whatsapp", notes: `Lang: ${lang}` });
    toast.success(t("lead_captured_toast"));
    const message = lang === "kn"
      ? `ನಮಸ್ಕಾರ, ರೋಸೆನ್ ${model.name} ಬಗ್ಗೆ ಹೆಚ್ಚಿನ ಮಾಹಿತಿ ಬೇಕು.`
      : `Hi Shri Shanteshwar Enterprises, I'm interested in the *${model.name}*. Please share more details.`;
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(message)}`, "_blank");
  };
  return (
    <Button onClick={handleClick} className={`w-full rounded-full bg-[#25D366] text-black hover:bg-[#20BF5B] font-semibold h-10 gap-2 ${className}`} data-testid={`wa-btn-${model.id}`}>
      <MessageCircle className="w-4 h-4" /> {t("whatsapp_enquire")}
    </Button>
  );
}

function VoucherDialog({ open, onOpenChange }) {
  const { t } = useI18n();
  const [step, setStep] = useState("input"); // input | verified | done
  const [code, setCode] = useState("");
  const [voucher, setVoucher] = useState(null);
  const [redeemer, setRedeemer] = useState({ name: "", phone: "", model_name: "" });
  const [busy, setBusy] = useState(false);

  const reset = () => { setStep("input"); setCode(""); setVoucher(null); setRedeemer({ name: "", phone: "", model_name: "" }); };

  const verify = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.get(`/vouchers/verify/${code.trim().toUpperCase()}`);
      setVoucher(data);
      setStep("verified");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invalid voucher");
    } finally {
      setBusy(false);
    }
  };

  const redeem = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/vouchers/redeem", {
        code: voucher.code,
        redeemer_name: redeemer.name,
        redeemer_phone: redeemer.phone,
        model_name: redeemer.model_name,
      });
      toast.success(`Voucher applied — ₹${data.discount_inr} off!`);
      setStep("done");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Redeem failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); onOpenChange(o); }}>
      <DialogContent className="bg-[#111] border-white/10 text-white sm:max-w-md" data-testid="voucher-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2"><Ticket className="w-5 h-5 text-[#00FF66]" /> Redeem your voucher</DialogTitle>
          <p className="text-xs text-white/50 mt-1">A referrer shared a Rosen voucher with you — apply ₹500 off any model.</p>
        </DialogHeader>
        {step === "input" && (
          <form onSubmit={verify} className="space-y-4">
            <div>
              <Label className="text-xs text-white/60 uppercase tracking-[0.2em]">Voucher code</Label>
              <Input required autoFocus placeholder="ROSEN-XXXXXX" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} className="mt-2 bg-black/40 border-white/10 font-mono tracking-wider" data-testid="voucher-code-input" />
            </div>
            <DialogFooter>
              <Button type="submit" disabled={busy} className="rounded-full bg-[#00FF66] text-black hover:bg-[#00E55C] font-semibold" data-testid="voucher-verify-btn">
                {busy ? "…" : "Verify"}
              </Button>
            </DialogFooter>
          </form>
        )}
        {step === "verified" && voucher && (
          <form onSubmit={redeem} className="space-y-4">
            <div className="p-4 rounded-lg bg-[#00FF66]/5 border border-[#00FF66]/30" data-testid="voucher-verified">
              <div className="text-xs uppercase tracking-[0.2em] text-[#00FF66] font-bold">Voucher valid</div>
              <div className="font-display font-black text-3xl text-[#00FF66] mt-2">₹{voucher.discount_inr} off</div>
              <div className="text-sm text-white/70 mt-1">Referred by <span className="text-white font-semibold">{voucher.referrer_name}</span></div>
            </div>
            <div>
              <Label className="text-xs text-white/60 uppercase tracking-[0.2em]">Your name</Label>
              <Input required value={redeemer.name} onChange={(e) => setRedeemer({ ...redeemer, name: e.target.value })} className="mt-2 bg-black/40 border-white/10" data-testid="voucher-redeemer-name" />
            </div>
            <div>
              <Label className="text-xs text-white/60 uppercase tracking-[0.2em]">Phone</Label>
              <Input required value={redeemer.phone} onChange={(e) => setRedeemer({ ...redeemer, phone: e.target.value })} className="mt-2 bg-black/40 border-white/10" data-testid="voucher-redeemer-phone" />
            </div>
            <div>
              <Label className="text-xs text-white/60 uppercase tracking-[0.2em]">Model you're buying (optional)</Label>
              <Input value={redeemer.model_name} onChange={(e) => setRedeemer({ ...redeemer, model_name: e.target.value })} placeholder="e.g. Rosen Q7" className="mt-2 bg-black/40 border-white/10" data-testid="voucher-redeemer-model" />
            </div>
            <DialogFooter>
              <Button type="submit" disabled={busy} className="rounded-full bg-[#00FF66] text-black hover:bg-[#00E55C] font-semibold" data-testid="voucher-claim-btn">
                {busy ? "…" : "Claim discount"}
              </Button>
            </DialogFooter>
          </form>
        )}
        {step === "done" && (
          <div className="text-center py-4">
            <CheckCircle2 className="w-14 h-14 text-[#00FF66] mx-auto mb-4" />
            <div className="font-display font-black text-2xl">You're all set!</div>
            <div className="text-sm text-white/60 mt-2">Show this confirmation at the showroom or WhatsApp us on 8722915521 to book delivery with your ₹500 discount applied.</div>
            <Button onClick={() => onOpenChange(false)} className="mt-6 rounded-full bg-[#00FF66] text-black hover:bg-[#00E55C] font-semibold" data-testid="voucher-done-btn">Done</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}


function TestRideDialog({ model, open, onOpenChange }) {
  const { t } = useI18n();
  const [form, setForm] = useState({ name: "", phone: "", preferred_date: todayISO(1), preferred_time: "10:00 AM", notes: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/test-rides", {
        ...form,
        model_id: model.id,
        model_name: model.name,
        city: "Basavakalyan",
      });
      toast.success(t("tr_success"));
      onOpenChange(false);
      setForm({ name: "", phone: "", preferred_date: todayISO(1), preferred_time: "10:00 AM", notes: "" });
    } catch (err) {
      toast.error("Please check the details and try again.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#111] border-white/10 text-white sm:max-w-md" data-testid="test-ride-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">{t("tr_dialog_title")}</DialogTitle>
          <p className="text-xs text-white/50 mt-1">{model?.name} · {t("tr_dialog_sub")}</p>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-white/60 uppercase tracking-[0.2em]">{t("tr_name")}</Label>
              <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-2 bg-black/40 border-white/10" data-testid="tr-name" />
            </div>
            <div>
              <Label className="text-xs text-white/60 uppercase tracking-[0.2em]">{t("tr_phone")}</Label>
              <Input required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-2 bg-black/40 border-white/10" data-testid="tr-phone" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-white/60 uppercase tracking-[0.2em]">{t("tr_date")}</Label>
              <Input type="date" required min={todayISO(0)} value={form.preferred_date} onChange={(e) => setForm({ ...form, preferred_date: e.target.value })} className="mt-2 bg-black/40 border-white/10 [color-scheme:dark]" data-testid="tr-date" />
            </div>
            <div>
              <Label className="text-xs text-white/60 uppercase tracking-[0.2em]">{t("tr_time")}</Label>
              <Select value={form.preferred_time} onValueChange={(v) => setForm({ ...form, preferred_time: v })}>
                <SelectTrigger className="mt-2 bg-black/40 border-white/10" data-testid="tr-time">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#111] border-white/10 text-white">
                  {TIME_SLOTS.map((s) => (<SelectItem key={s} value={s}>{s}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-xs text-white/60 uppercase tracking-[0.2em]">{t("tr_notes")}</Label>
            <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-2 bg-black/40 border-white/10" data-testid="tr-notes" rows={2} />
          </div>
          <DialogFooter>
            <Button type="submit" disabled={busy} className="rounded-full bg-[#00FF66] text-black hover:bg-[#00E55C] font-semibold px-6" data-testid="tr-submit">
              <CalendarDays className="w-4 h-4 mr-2" /> {busy ? "…" : t("tr_submit")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function CompareDialog({ models, open, onOpenChange }) {
  const { t } = useI18n();
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#111] border-white/10 text-white max-w-5xl" data-testid="compare-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">{t("compare_title")}</DialogTitle>
        </DialogHeader>
        <div className="overflow-x-auto">
          <div className="grid gap-4" style={{ gridTemplateColumns: `160px repeat(${models.length}, minmax(180px, 1fr))` }}>
            {/* Header row: image */}
            <div className="text-xs uppercase tracking-[0.2em] text-white/40 font-bold self-end pb-2">Model</div>
            {models.map((m) => (
              <div key={`img-${m.id}`} className="rounded-lg overflow-hidden border border-white/10 bg-white">
                <img src={m.image} alt={m.name} className="w-full h-32 object-contain" />
              </div>
            ))}

            {[
              { label: "Name", getV: (m) => <span className="font-display font-bold text-lg">{m.name}</span> },
              { label: t("battery"), getV: (m) => m.battery },
              { label: t("range"), getV: (m) => m.range_text },
              { label: t("ex_showroom") + " " + "Price", getV: (m) => m.price_inr ? <span className="font-display font-black text-[#00FF66] text-xl">{formatINR(m.price_inr)}</span> : <span className="text-white/60">{t("price_on_request")}</span> },
              { label: t("save"), getV: (m) => m.discount_inr ? formatINR(m.discount_inr) : "—" },
              { label: t("monthly_emi") + " (24mo, 20% down)", getV: (m) => m.price_inr ? <span className="text-[#00FF66] font-bold">{formatINR(calcEmi(m.price_inr, 24, 20))}{t("per_month")}</span> : "—" },
            ].map((row) => (
              <React.Fragment key={row.label}>
                <div className="text-xs uppercase tracking-[0.15em] text-white/50 font-bold py-3 border-t border-white/5">{row.label}</div>
                {models.map((m) => (
                  <div key={`${row.label}-${m.id}`} className="py-3 border-t border-white/5 text-sm text-white/85">{row.getV(m)}</div>
                ))}
              </React.Fragment>
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ModelCard({ model, idx, isSelected, onToggleCompare, onBookRide }) {
  const { t } = useI18n();
  const priced = typeof model.price_inr === "number";
  return (
    <div
      className={`group rounded-xl border bg-[#111111] overflow-hidden hover:-translate-y-1 transition-[transform,border-color] duration-200 flex flex-col ${isSelected ? "border-[#00FF66]" : "border-white/10 hover:border-[#00FF66]/50"}`}
      data-testid={`model-card-${idx}`}
    >
      <div className="aspect-[4/3] overflow-hidden bg-white relative">
        <img src={model.image} alt={model.name} className="w-full h-full object-contain group-hover:scale-[1.03] transition-transform duration-500" loading="lazy" />
        {model.discount_inr ? (
          <div className="absolute top-3 right-3 bg-[#00FF66] text-black text-[10px] font-black tracking-wider px-2 py-1 rounded">
            {t("save")} {formatINR(model.discount_inr)}
          </div>
        ) : null}
        <label className="absolute top-3 left-3 flex items-center gap-2 bg-black/70 backdrop-blur-md border border-white/15 rounded-full px-2.5 py-1 cursor-pointer hover:border-[#00FF66]/60 transition-colors">
          <Checkbox checked={isSelected} onCheckedChange={() => onToggleCompare(model)} className="border-white/40 data-[state=checked]:bg-[#00FF66] data-[state=checked]:text-black data-[state=checked]:border-[#00FF66] h-4 w-4" data-testid={`compare-check-${model.id}`} />
          <span className="text-[10px] uppercase tracking-[0.15em] font-bold text-white/80">{isSelected ? t("added_to_compare") : t("add_to_compare")}</span>
        </label>
      </div>
      <div className="p-5 flex flex-col flex-1">
        <div className="text-[10px] uppercase tracking-[0.2em] text-[#00FF66] font-bold mb-2">{model.battery} {t("battery")}</div>
        <div className="font-display font-bold text-lg leading-tight" data-testid={`model-name-${idx}`}>{model.name}</div>
        <div className="mt-3 text-xs text-white/60">{t("range")} · <span className="text-white font-medium">{model.range_text}</span></div>
        {priced ? (
          <>
            <div className="mt-4 flex items-baseline justify-between">
              <div className="font-display font-black text-2xl">{formatINR(model.price_inr)}</div>
              <div className="text-xs text-white/40">{t("ex_showroom")}</div>
            </div>
            <EmiWidget price={model.price_inr} />
          </>
        ) : (
          <div className="mt-4 mb-4 py-3 px-4 rounded-lg bg-white/5 border border-white/10 text-center">
            <div className="font-display font-black text-lg text-[#00FF66]">{t("price_on_request")}</div>
          </div>
        )}
        <div className="mt-4 grid grid-cols-2 gap-2">
          <Button
            onClick={() => onBookRide(model)}
            variant="outline"
            className="rounded-full border-white/20 bg-transparent text-white hover:bg-white/10 hover:text-[#00FF66] hover:border-[#00FF66]/50 h-10 text-xs gap-1"
            data-testid={`book-ride-${model.id}`}
          >
            <CalendarDays className="w-3.5 h-3.5" /> {t("book_test_ride")}
          </Button>
          <WhatsAppButton model={model} className="h-10 text-xs" />
        </div>
      </div>
    </div>
  );
}

export default function Landing() {
  const { t } = useI18n();
  const [models, setModels] = useState([]);
  const [board, setBoard] = useState([]);
  const [contest, setContest] = useState(null);
  const [winners, setWinners] = useState(null);
  const [reels, setReels] = useState([]);
  const [reelOpen, setReelOpen] = useState(null);
  const [selected, setSelected] = useState([]); // array of model objects (max 3)
  const [compareOpen, setCompareOpen] = useState(false);
  const [rideModel, setRideModel] = useState(null);
  const [voucherOpen, setVoucherOpen] = useState(false);

  useEffect(() => {
    api.get("/models").then((r) => setModels(r.data)).catch(() => {});
    api.get("/leaderboard").then((r) => setBoard(r.data)).catch(() => {});
    api.get("/contest/current").then((r) => setContest(r.data)).catch(() => {});
    api.get("/contest/latest-winners").then((r) => r.data.has_winners && setWinners(r.data)).catch(() => {});
    api.get("/reels").then((r) => setReels(r.data)).catch(() => {});
  }, []);

  const toggleCompare = (m) => {
    setSelected((prev) => {
      const exists = prev.find((x) => x.id === m.id);
      if (exists) return prev.filter((x) => x.id !== m.id);
      if (prev.length >= 3) {
        toast.error("You can compare up to 3 models at once");
        return prev;
      }
      return [...prev, m];
    });
  };

  const rewards = [
    { role: t("reward_customer"), amount: "₹3,000", perk: t("reward_customer_perk") },
    { role: t("reward_sse"), amount: "₹5,000", perk: t("reward_sse_perk") },
    { role: t("reward_influencer"), amount: "₹4,000", perk: t("reward_influencer_perk") },
  ];
  const stepsData = [
    { i: <Users className="w-5 h-5" />, t: t("step_1_t"), d: t("step_1_d") },
    { i: <Bike className="w-5 h-5" />, t: t("step_2_t"), d: t("step_2_d") },
    { i: <ShieldCheck className="w-5 h-5" />, t: t("step_3_t"), d: t("step_3_d") },
    { i: <Wallet className="w-5 h-5" />, t: t("step_4_t"), d: t("step_4_d") },
  ];

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white noise">
      <Header />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img src={HERO_IMG} alt="EV motorcycle" className="w-full h-full object-cover opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/70 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A] via-transparent to-transparent" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 pt-24 pb-32 lg:pt-32 lg:pb-40 grid lg:grid-cols-12 gap-8">
          <div className="lg:col-span-8 fade-up">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#00FF66]/40 bg-[#00FF66]/10 text-[#00FF66] text-xs tracking-[0.2em] font-bold uppercase mb-8">
              <BatteryCharging className="w-3.5 h-3.5" /> {t("hero_pill")}
            </div>
            <h1 className="font-display font-black text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tighter">
              {t("hero_l1")}<br />
              <span className="text-[#00FF66]">{t("hero_l2")}</span><br />
              {t("hero_l3")}
            </h1>
            <p className="mt-8 max-w-xl text-lg text-white/70 leading-relaxed">{t("hero_sub")}</p>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link to="/register" data-testid="hero-join-cta">
                <Button className="rounded-full bg-[#00FF66] text-black hover:bg-[#00E55C] font-semibold px-8 h-12 text-base">
                  {t("hero_cta_primary")} <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <a href="#models" data-testid="hero-explore-cta">
                <Button variant="outline" className="rounded-full border-white/30 bg-transparent hover:bg-white/10 text-white hover:text-white px-8 h-12">
                  {t("hero_cta_secondary")}
                </Button>
              </a>
              <button
                onClick={() => setVoucherOpen(true)}
                className="inline-flex items-center gap-2 text-sm text-white/70 hover:text-[#00FF66] transition-colors border-b border-dashed border-white/20 hover:border-[#00FF66] pb-1"
                data-testid="hero-voucher-link"
              >
                <Ticket className="w-4 h-4" /> Have a voucher?
              </button>
            </div>
            <div className="mt-16 grid grid-cols-3 gap-6 max-w-2xl">
              {[
                { k: "₹50L+", v: t("stat_paid") },
                { k: "4,200+", v: t("stat_sold") },
                { k: "28", v: t("stat_cities") },
              ].map((s) => (
                <div key={s.v} className="border-l border-[#00FF66]/40 pl-4">
                  <div className="font-display font-black text-3xl">{s.k}</div>
                  <div className="text-xs uppercase tracking-[0.2em] text-white/50 mt-1">{s.v}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="lg:col-span-4 hidden lg:flex items-end justify-end">
            <div className="w-full bg-black/60 backdrop-blur-xl border border-white/10 rounded-2xl p-6 space-y-4">
              <div className="text-xs uppercase tracking-[0.3em] text-[#00FF66] font-bold">{t("rewards_title")}</div>
              {rewards.map((r) => (
                <div key={r.role} className="flex items-center justify-between border-b border-white/5 pb-3 last:border-0">
                  <div>
                    <div className="font-semibold">{r.role}</div>
                    <div className="text-xs text-white/50">{r.perk}</div>
                  </div>
                  <div className="font-display font-black text-2xl text-[#00FF66]">{r.amount}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* HOW */}
      <section id="how" className="max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <div className="text-xs uppercase tracking-[0.3em] text-[#00FF66] font-bold mb-4">{t("how_kicker")}</div>
            <h2 className="font-display font-black text-4xl lg:text-5xl leading-tight">{t("how_title_1")}<br />{t("how_title_2")}</h2>
            <p className="mt-6 text-white/60">{t("how_sub")}</p>
          </div>
          <div className="lg:col-span-8 grid sm:grid-cols-2 gap-4">
            {stepsData.map((s, idx) => (
              <div key={s.t} className="p-6 rounded-xl border border-white/10 bg-[#111111] hover:border-[#00FF66]/50 hover:-translate-y-1 transition-[transform,border-color] duration-200" data-testid={`how-step-${idx + 1}`}>
                <div className="w-10 h-10 rounded-lg bg-[#00FF66]/10 text-[#00FF66] flex items-center justify-center mb-4">{s.i}</div>
                <div className="text-xs uppercase tracking-[0.2em] text-white/40 font-bold mb-2">Step {idx + 1}</div>
                <div className="font-display text-xl font-bold">{s.t}</div>
                <div className="text-sm text-white/60 mt-2 leading-relaxed">{s.d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WINNERS HERO */}
      {winners && winners.winners?.length > 0 && (
        <section className="border-t border-white/5 bg-gradient-to-b from-[#0d0d0d] to-[#0A0A0A]">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
            <div className="flex items-center gap-3 mb-8">
              <Crown className="w-6 h-6 text-[#FFD700]" />
              <div className="text-xs uppercase tracking-[0.3em] text-[#FFD700] font-bold">Champions of {winners.contest_name?.replace(" Referral Contest", "")}</div>
            </div>
            <h2 className="font-display font-black text-4xl lg:text-5xl mb-10">
              Last month's <span className="text-[#00FF66]">top referrers</span>.
            </h2>
            <div className="grid md:grid-cols-3 gap-5" data-testid="winners-grid">
              {winners.winners.map((w) => (
                <div
                  key={w.rank}
                  className={`relative p-6 rounded-2xl border overflow-hidden ${
                    w.rank === 1
                      ? "border-[#FFD700]/50 bg-gradient-to-br from-[#FFD700]/10 to-[#111]"
                      : w.rank === 2
                      ? "border-[#C0C0C0]/40 bg-gradient-to-br from-[#C0C0C0]/8 to-[#111]"
                      : "border-[#CD7F32]/40 bg-gradient-to-br from-[#CD7F32]/10 to-[#111]"
                  }`}
                  data-testid={`winner-${w.rank}`}
                >
                  <div className="absolute -right-6 -top-6 w-32 h-32 rounded-full blur-3xl"
                       style={{ backgroundColor: w.rank === 1 ? "#FFD70020" : w.rank === 2 ? "#C0C0C020" : "#CD7F3220" }} />
                  <div className="relative">
                    <div className="flex items-start justify-between mb-6">
                      <div
                        className="font-display font-black text-6xl"
                        style={{ color: w.rank === 1 ? "#FFD700" : w.rank === 2 ? "#C0C0C0" : "#CD7F32" }}
                      >
                        #{w.rank}
                      </div>
                      <Trophy
                        className="w-8 h-8"
                        style={{ color: w.rank === 1 ? "#FFD700" : w.rank === 2 ? "#C0C0C0" : "#CD7F32" }}
                      />
                    </div>
                    <div className="font-display font-bold text-2xl mb-1">{w.name}</div>
                    <div className="text-xs uppercase tracking-[0.2em] text-white/50 font-bold mb-6">{w.role || "referrer"}</div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <div className="text-[10px] uppercase tracking-[0.15em] text-white/40 font-bold">Sales</div>
                        <div className="font-display font-black text-2xl text-white">{w.conversions}</div>
                      </div>
                      <div>
                        <div className="text-[10px] uppercase tracking-[0.15em] text-white/40 font-bold">Prize won</div>
                        <div className="font-display font-black text-2xl text-[#00FF66]">{formatINR(w.prize)}</div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}


      {/* REELS — Real rider testimonials */}
      {reels.length > 0 && (
        <section id="reels" className="max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32">
          <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
            <div>
              <div className="text-xs uppercase tracking-[0.3em] text-[#00FF66] font-bold mb-3">Real Riders · Real Reviews</div>
              <h2 className="font-display font-black text-4xl lg:text-5xl">See Rosen <span className="text-[#00FF66]">in real life</span>.</h2>
              <p className="text-white/50 mt-3 text-sm max-w-lg">30-second clips from customers across Karnataka. No paid actors — just people riding to work every day.</p>
            </div>
            <div className="text-xs text-white/40 uppercase tracking-[0.2em]">Swipe →</div>
          </div>
          <div className="overflow-x-auto pb-6 -mx-6 lg:-mx-10 px-6 lg:px-10">
            <div className="flex gap-5" style={{ minWidth: "min-content" }}>
              {reels.map((r, idx) => (
                <button
                  key={r.id}
                  onClick={() => setReelOpen(r)}
                  className="group relative flex-shrink-0 w-64 aspect-[9/16] rounded-2xl overflow-hidden border border-white/10 hover:border-[#00FF66]/50 transition-all duration-300 hover:-translate-y-1"
                  data-testid={`reel-card-${idx}`}
                >
                  <img src={r.poster_url} alt={r.title} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent" />
                  <div className="absolute top-4 right-4 w-11 h-11 rounded-full bg-[#00FF66] flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                    <Play className="w-5 h-5 text-black fill-current ml-0.5" />
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 p-4 text-left">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-[#00FF66] font-bold mb-1">{r.model_name}</div>
                    <div className="font-display font-black text-lg leading-tight text-white">{r.title}</div>
                    <div className="text-xs text-white/70 mt-1">
                      {r.customer_name}{r.city ? ` · ${r.city}` : ""}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </section>
      )}


      {/* MODELS */}
      <section id="models" className="border-t border-white/5 bg-[#0d0d0d]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32">
          <div className="flex flex-wrap items-end justify-between gap-4 mb-12">
            <div>
              <div className="text-xs uppercase tracking-[0.3em] text-[#00FF66] font-bold mb-3">{t("models_kicker")}</div>
              <h2 className="font-display font-black text-4xl lg:text-5xl">{t("models_title_1")} <span className="text-[#00FF66]">{t("models_title_2")}</span></h2>
              <p className="text-white/50 mt-3 text-sm">{t("models_sub")}</p>
            </div>
            <div className="text-right text-xs text-white/50 space-y-1">
              <div className="uppercase tracking-[0.2em] text-[#00FF66] font-bold">{t("showroom")}</div>
              <div className="text-white/80">Basavakalyan</div>
              <div>Sales · 8722915521 / 8880856856</div>
            </div>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 pb-24">
            {models.map((m, idx) => (
              <ModelCard
                key={m.id}
                model={m}
                idx={idx}
                isSelected={!!selected.find((x) => x.id === m.id)}
                onToggleCompare={toggleCompare}
                onBookRide={setRideModel}
              />
            ))}
          </div>
        </div>
      </section>

      {/* LEADERBOARD */}
      <section id="leaderboard" className="max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32">
        {contest?.contest?.active && (
          <div className="mb-12 rounded-2xl border border-[#00FF66]/30 bg-gradient-to-r from-[#0F1A0F] to-[#0d0d0d] p-6 md:p-8 relative overflow-hidden" data-testid="contest-strip">
            <div className="absolute -right-10 -top-10 w-56 h-56 rounded-full bg-[#00FF66]/10 blur-3xl" />
            <div className="relative flex flex-wrap items-center justify-between gap-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-lg bg-[#00FF66]/15 flex items-center justify-center">
                  <Sparkles className="w-6 h-6 text-[#00FF66]" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-[0.3em] text-[#00FF66] font-bold mb-1">Contest · Live now</div>
                  <div className="font-display font-black text-2xl">{contest.contest.name}</div>
                  <div className="text-sm text-white/70 mt-1">
                    Top referrers this month win bonus cash prizes
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-3 text-center">
                {contest.contest.prizes.map((p, i) => (
                  <div key={`prize-${i + 1}`} className={`px-4 py-3 rounded-lg border ${i === 0 ? "border-[#00FF66] bg-[#00FF66]/10" : "border-white/10 bg-white/[0.03]"}`}>
                    <div className="text-[10px] uppercase tracking-[0.2em] text-white/50 font-bold">#{i + 1}</div>
                    <div className={`font-display font-black text-xl ${i === 0 ? "text-[#00FF66]" : "text-white"}`}>{formatINR(p)}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <div className="text-xs uppercase tracking-[0.3em] text-[#00FF66] font-bold mb-4">{t("lb_kicker")}</div>
            <h2 className="font-display font-black text-4xl lg:text-5xl leading-tight">{t("lb_title_1")} <span className="text-[#00FF66]">{t("lb_title_2")}</span>.</h2>
            <p className="mt-6 text-white/60 max-w-md">{t("lb_sub")}</p>
            <Link to="/register" className="inline-block mt-8" data-testid="leaderboard-cta">
              <Button className="rounded-full bg-[#00FF66] text-black hover:bg-[#00E55C] font-semibold px-6">
                {t("lb_cta")} <Trophy className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
          <div className="lg:col-span-7 rounded-2xl border border-white/10 overflow-hidden">
            <div className="grid grid-cols-12 px-6 py-4 text-xs uppercase tracking-[0.2em] text-white/40 font-bold bg-[#111]">
              <div className="col-span-1">#</div>
              <div className="col-span-5">{t("lb_col_referrer")}</div>
              <div className="col-span-2 text-right">{t("lb_col_role")}</div>
              <div className="col-span-2 text-right">{t("lb_col_sales")}</div>
              <div className="col-span-2 text-right">{t("lb_col_earned")}</div>
            </div>
            {board.length === 0 ? (
              <div className="p-10 text-center text-white/40 text-sm" data-testid="leaderboard-empty">{t("lb_empty")}</div>
            ) : (
              board.map((row, idx) => (
                <div key={row.referral_code || idx} className="grid grid-cols-12 px-6 py-4 border-t border-white/5 items-center hover:bg-white/[0.02] transition-colors" data-testid={`leaderboard-row-${idx}`}>
                  <div className={`col-span-1 font-display font-black text-xl ${idx < 3 ? "text-[#00FF66]" : "text-white/40"}`}>{idx + 1}</div>
                  <div className="col-span-5">
                    <div className="font-semibold">{row.name}</div>
                    <div className="text-xs text-white/40">{row.referral_code}</div>
                  </div>
                  <div className="col-span-2 text-right text-xs uppercase tracking-wider text-white/60">{row.role}</div>
                  <div className="col-span-2 text-right font-mono">{row.conversions}</div>
                  <div className="col-span-2 text-right font-display font-bold text-[#00FF66]">{formatINR(row.earnings)}</div>
                </div>
              ))
            )}
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <section className="border-t border-white/5 bg-[#0d0d0d]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
          <div>
            <h3 className="font-display font-black text-3xl md:text-4xl">{t("footer_cta_title")}</h3>
            <p className="text-white/60 mt-3">{t("footer_cta_sub")}</p>
          </div>
          <Link to="/register" data-testid="footer-cta">
            <Button className="rounded-full bg-[#00FF66] text-black hover:bg-[#00E55C] font-semibold px-8 h-12 text-base">
              {t("footer_cta_btn")} <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
        <div className="border-t border-white/5">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 py-8 flex flex-col md:flex-row justify-between gap-4 text-sm text-white/40">
            <div>© {new Date().getFullYear()} Shri Shanteshwar Enterprises · Basavakalyan · {t("footer_rights")}</div>
            <div className="tracking-[0.2em] uppercase text-xs">{t("footer_care")} · 7996668560</div>
          </div>
        </div>
      </section>

      {/* Compare floating bar */}
      {selected.length > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 bg-[#111]/95 backdrop-blur-xl border border-[#00FF66]/40 rounded-full shadow-[0_10px_40px_rgba(0,255,102,0.15)] pl-3 pr-2 py-2 flex items-center gap-3 max-w-[95vw]" data-testid="compare-bar">
          <div className="flex items-center gap-2">
            {selected.map((m) => (
              <div key={m.id} className="flex items-center gap-1.5 bg-white/5 border border-white/10 rounded-full pl-2 pr-1 py-1">
                <span className="text-xs text-white/80 truncate max-w-[100px]">{m.name}</span>
                <button onClick={() => toggleCompare(m)} className="w-4 h-4 rounded-full bg-white/10 hover:bg-red-500/30 flex items-center justify-center" aria-label="Remove">
                  <X className="w-2.5 h-2.5" />
                </button>
              </div>
            ))}
          </div>
          <div className="text-xs text-white/50 hidden sm:block">
            {selected.length} {t("compare_bar")}
          </div>
          <Button
            onClick={() => selected.length >= 2 ? setCompareOpen(true) : toast.info(t("compare_pick_more"))}
            className="rounded-full bg-[#00FF66] text-black hover:bg-[#00E55C] font-semibold h-9 px-4 text-xs"
            data-testid="compare-view-btn"
          >
            <CheckCircle2 className="w-4 h-4 mr-1.5" />{t("compare_view")}
          </Button>
          <button onClick={() => setSelected([])} className="text-xs text-white/50 hover:text-white px-2" data-testid="compare-clear-btn">
            {t("compare_clear")}
          </button>
        </div>
      )}

      <CompareDialog models={selected} open={compareOpen} onOpenChange={setCompareOpen} />
      {rideModel && (
        <TestRideDialog model={rideModel} open={!!rideModel} onOpenChange={(o) => !o && setRideModel(null)} />
      )}
      <VoucherDialog open={voucherOpen} onOpenChange={setVoucherOpen} />

      {/* Reel player */}
      <Dialog open={!!reelOpen} onOpenChange={(o) => !o && setReelOpen(null)}>
        <DialogContent className="bg-black border-white/10 text-white sm:max-w-md p-0 overflow-hidden" data-testid="reel-dialog">
          {reelOpen && (
            <div className="relative aspect-[9/16] bg-black">
              {reelOpen.video_url ? (
                <video
                  src={reelOpen.video_url}
                  poster={reelOpen.poster_url}
                  className="w-full h-full object-cover"
                  autoPlay
                  playsInline
                  controls
                  data-testid="reel-video"
                />
              ) : (
                <>
                  <img src={reelOpen.poster_url} alt={reelOpen.title} className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
                  <div className="absolute top-4 left-4 right-4 flex items-start gap-2 px-3 py-2 rounded-lg bg-black/60 backdrop-blur border border-white/10 text-xs text-white/70">
                    🎬 Full video coming soon — a Rosen team member is shooting this at {reelOpen.city || "the showroom"}.
                  </div>
                </>
              )}
              <div className="absolute bottom-0 left-0 right-0 p-5">
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#00FF66] font-bold mb-1">{reelOpen.model_name}</div>
                <div className="font-display font-black text-2xl leading-tight">{reelOpen.title}</div>
                {reelOpen.quote && (
                  <div className="text-sm text-white/80 italic mt-3">"{reelOpen.quote}"</div>
                )}
                <div className="mt-3 text-xs text-white/60">
                  — {reelOpen.customer_name}{reelOpen.city ? `, ${reelOpen.city}` : ""}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
